import { TradeType } from '@uniswap/sdk-core'

import { VoteOption } from '../governance/types'

export interface SerializableTransactionReceipt {
  to: string
  from: string
  contractAddress: string
  transactionIndex: number
  blockHash: string
  transactionHash: string
  blockNumber: number
  status?: number
}

/**
 * Be careful adding to this enum, always assign a unique value (typescript will not prevent duplicate values).
 * These values is persisted in state and if you change the value it will cause errors
 */
export enum TransactionType {
  APPROVAL = 0,
  SWAP,
  DEPOSIT_LIQUIDITY_STAKING,
  WITHDRAW_LIQUIDITY_STAKING,
  CLAIM,
  VOTE,
  DELEGATE,
  WRAP,
  CREATE_V3_POOL,
  ADD_LIQUIDITY_V3_POOL,
  ADD_LIQUIDITY_V2_POOL,
  MIGRATE_LIQUIDITY_V3,
  COLLECT_FEES,
  REMOVE_LIQUIDITY_V3,
  SUBMIT_PROPOSAL,
  QUEUE,
  EXECUTE,
  BUY,
  SEND,
  RECEIVE,
  MINT,
  BURN,
  BORROW,
  REPAY,
  DEPLOY,
  CANCEL,
  ADD_BRIBES,
  RESET_VEROX,
  VOTE_VEROX,
  CLAIM_REWARDS,
  DEPOSIT,
  WITHDRAW,
  CLOSE_POSITION,
  EDIT,
  EXECUTE_ORDERLIST,
  CREATE_LOCK,
  MERGE_LOCK,
  ADD_LOCK,
  REMOVE_LOCK,
  SPLIT_LOCK,
  TRADE,
}

interface BaseTransactionInfo {
  type: TransactionType
}

export interface VoteTransactionInfo extends BaseTransactionInfo {
  type: TransactionType.VOTE
  governorAddress: string
  proposalId: number
  decision: VoteOption
  reason: string
}

export interface QueueTransactionInfo extends BaseTransactionInfo {
  type: TransactionType.QUEUE
  governorAddress: string
  proposalId: number
}

export interface ExecuteTransactionInfo extends BaseTransactionInfo {
  type: TransactionType.EXECUTE
  governorAddress: string
  proposalId: number
}

export interface DelegateTransactionInfo extends BaseTransactionInfo {
  type: TransactionType.DELEGATE
  delegatee: string
}

export interface ApproveTransactionInfo extends BaseTransactionInfo {
  type: TransactionType.APPROVAL
  tokenAddress: string
  spender: string
  amount: string
}

interface BaseSwapTransactionInfo extends BaseTransactionInfo {
  type: TransactionType.SWAP
  tradeType: TradeType
  inputCurrencyId: string
  outputCurrencyId: string
  isUniswapXOrder: boolean
}

export interface ExactInputSwapTransactionInfo extends BaseSwapTransactionInfo {
  tradeType: TradeType.EXACT_INPUT
  inputCurrencyAmountRaw: string
  expectedOutputCurrencyAmountRaw: string
  minimumOutputCurrencyAmountRaw: string
  settledOutputCurrencyAmountRaw?: string
}
export interface ExactOutputSwapTransactionInfo extends BaseSwapTransactionInfo {
  tradeType: TradeType.EXACT_OUTPUT
  outputCurrencyAmountRaw: string
  expectedInputCurrencyAmountRaw: string
  maximumInputCurrencyAmountRaw: string
}

interface DepositLiquidityStakingTransactionInfo {
  type: TransactionType.DEPOSIT_LIQUIDITY_STAKING
  token0Address: string
  token1Address: string
}

interface AddBribesTransactionInfo {
  type: TransactionType.ADD_BRIBES
  token0Address: string
  token1Address: string
}

interface ResetVeroxTransactionInfo {
  type: TransactionType.RESET_VEROX
  token0Address: string
  token1Address: string
}

interface VoteVeroxTransactionInfo {
  type: TransactionType.VOTE_VEROX
  token0Address: string
  token1Address: string
}

interface ClaimRewardsTransactionInfo {
  type: TransactionType.CLAIM_REWARDS
  token0Address: string
  token1Address: string
}

interface DepositTransactionInfo {
  type: TransactionType.DEPOSIT
  token0Address: string
  token1Address: string
}

interface WithdrawTransactionInfo {
  type: TransactionType.WITHDRAW
  token0Address: string
  token1Address: string
}

interface ClosePositionTransactionInfo {
  type: TransactionType.CLOSE_POSITION
  token0Address: string
  token1Address: string
}

interface EditTransactionInfo {
  type: TransactionType.EDIT
  token0Address: string
  token1Address: string
}
interface CreateLockTransactionInfo {
  type: TransactionType.CREATE_LOCK
  token0Address: string
  token1Address: string
}

interface ExecuteOrderListTransactionInfo {
  type: TransactionType.EXECUTE_ORDERLIST
  token0Address: string
  token1Address: string
}
interface MergeLockTransactionInfo {
  type: TransactionType.MERGE_LOCK
  token0Address: string
  token1Address: string
}
interface AddLockTransactionInfo {
  type: TransactionType.ADD_LOCK
  token0Address: string
  token1Address: string
}

interface RemoveLockTransactionInfo {
  type: TransactionType.REMOVE_LOCK
  token0Address: string
  token1Address: string
}

interface SplitLockTransactionInfo {
  type: TransactionType.SPLIT_LOCK
  token0Address: string
  token1Address: string
}

interface TRADETransactionInfo {
  type: TransactionType.TRADE
  token0Address: string
  token1Address: string
}

interface WithdrawLiquidityStakingTransactionInfo {
  type: TransactionType.WITHDRAW_LIQUIDITY_STAKING
  token0Address: string
  token1Address: string
}

export interface WrapTransactionInfo {
  type: TransactionType.WRAP
  unwrapped: boolean
  currencyAmountRaw: string
  chainId?: number
}

export interface ClaimTransactionInfo {
  type: TransactionType.CLAIM
  recipient: string
  uniAmountRaw?: string
}

export interface CreateV3PoolTransactionInfo {
  type: TransactionType.CREATE_V3_POOL
  baseCurrencyId: string
  quoteCurrencyId: string
}

export interface AddLiquidityV3PoolTransactionInfo {
  type: TransactionType.ADD_LIQUIDITY_V3_POOL
  createPool: boolean
  baseCurrencyId: string
  quoteCurrencyId: string
  feeAmount: number
  expectedAmountBaseRaw: string
  expectedAmountQuoteRaw: string
}

export interface AddLiquidityV2PoolTransactionInfo {
  type: TransactionType.ADD_LIQUIDITY_V2_POOL
  baseCurrencyId: string
  quoteCurrencyId: string
  expectedAmountBaseRaw: string
  expectedAmountQuoteRaw: string
}

export interface MigrateV2LiquidityToV3TransactionInfo {
  type: TransactionType.MIGRATE_LIQUIDITY_V3
  baseCurrencyId: string
  quoteCurrencyId: string
  isFork: boolean
}

export interface CollectFeesTransactionInfo {
  type: TransactionType.COLLECT_FEES
  currencyId0: string
  currencyId1: string
  expectedCurrencyOwed0: string
  expectedCurrencyOwed1: string
}

export interface RemoveLiquidityV3TransactionInfo {
  type: TransactionType.REMOVE_LIQUIDITY_V3
  baseCurrencyId: string
  quoteCurrencyId: string
  expectedAmountBaseRaw: string
  expectedAmountQuoteRaw: string
}

interface SubmitProposalTransactionInfo {
  type: TransactionType.SUBMIT_PROPOSAL
}

export type TransactionInfo =
  | ApproveTransactionInfo
  | ExactOutputSwapTransactionInfo
  | ExactInputSwapTransactionInfo
  | ClaimTransactionInfo
  | VoteTransactionInfo
  | QueueTransactionInfo
  | ExecuteTransactionInfo
  | DelegateTransactionInfo
  | DepositLiquidityStakingTransactionInfo
  | AddBribesTransactionInfo
  | ResetVeroxTransactionInfo
  | VoteVeroxTransactionInfo
  | ClaimRewardsTransactionInfo
  | DepositTransactionInfo
  | WithdrawTransactionInfo
  | ClosePositionTransactionInfo
  | EditTransactionInfo
  | ExecuteOrderListTransactionInfo
  | CreateLockTransactionInfo
  | MergeLockTransactionInfo
  | AddLockTransactionInfo
  | RemoveLockTransactionInfo
  | SplitLockTransactionInfo
  | WithdrawLiquidityStakingTransactionInfo
  | WrapTransactionInfo
  | CreateV3PoolTransactionInfo
  | AddLiquidityV3PoolTransactionInfo
  | AddLiquidityV2PoolTransactionInfo
  | MigrateV2LiquidityToV3TransactionInfo
  | CollectFeesTransactionInfo
  | RemoveLiquidityV3TransactionInfo
  | SubmitProposalTransactionInfo
  | TRADETransactionInfo

export interface TransactionDetails {
  hash: string
  receipt?: SerializableTransactionReceipt
  lastCheckedBlockNumber?: number
  addedTime: number
  confirmedTime?: number
  deadline?: number
  from: string
  info: TransactionInfo
  nonce?: number
}
